import * as React from 'react';
import { createPortal } from 'react-dom';
import styled, { ThemeContext } from 'styled-components';
import { usePopper } from 'react-popper';
import { Transition } from 'react-transition-group';

import { ModalWrapper } from '../Modal/Modal';

import { Caret } from './Caret';
import { MeasureHelper } from './measure';

type TransitionState = 'exited' | 'entering' | 'entered' | 'exiting';

export type PopupPlacement =
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'left'
    | 'left-start'
    | 'left-end'
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end';

export type PseudoPlacement = 'top' | 'bottom' | 'right' | 'left';

export type PopupProps = {
    isOpen: boolean;
    parentRef: React.RefObject<HTMLElement>;
    onClose?: () => void;
    placement?: PopupPlacement;
    arrowSize?: number;
    portalRoot?: string;
    fullWidth?: boolean;
    hasCaret?: boolean;
    hasShadow?: boolean;
};

type ContainerProps = {
    state: TransitionState;
    placement: PseudoPlacement;
    hasShadow: boolean;
    hasCaret: boolean;
    fullWidth: boolean;
};

const isExitState = (state: TransitionState) => ['exiting', 'exited'].indexOf(state) > -1;

const shadowWithCaretMapping = {
    top: `0px 8px 24px rgba(0, 0, 0, 0.3), 0px 2px 4px rgba(0, 0, 0, 0.3), 0px -3px 12px rgba(0, 0, 0, 0.2)`,
    bottom: `0px -8px 24px rgba(0, 0, 0, 0.3), 0px -2px 4px rgba(0, 0, 0, 0.3), 0px 3px 12px rgba(0, 0, 0, 0.2)`,
    left: `8px 0px 24px rgba(0, 0, 0, 0.3), 2px 0px 4px rgba(0, 0, 0, 0.3), -3px 0px 12px rgba(0, 0, 0, 0.2)`,
    right: `-8px 0px 24px rgba(0, 0, 0, 0.3), -2px 0px 4px rgba(0, 0, 0, 0.3), 3px 0px 12px rgba(0, 0, 0, 0.2)`,
};

const getShadowWithCaret = (position: PseudoPlacement) => shadowWithCaretMapping[position];

const shadowWithoutCaretMapping = {
    top: '0px -8px 24px rgba(0, 0, 0, 0.3), 0px -2px 4px rgba(0, 0, 0, 0.3)',
    bottom: '0px 8px 24px rgba(0, 0, 0, 0.3), 0px 2px 4px rgba(0, 0, 0, 0.3)',
    left: '-8px 0px 24px rgba(0, 0, 0, 0.3), -2px 0px 4px rgba(0, 0, 0, 0.3)',
    right: '8px 0px 24px rgba(0, 0, 0, 0.3), 2px 0px 4px rgba(0, 0, 0, 0.3)',
};

const getShadowWithoutCaret = (position: PseudoPlacement) => shadowWithoutCaretMapping[position];

const getShadow = (position: PseudoPlacement, hasCaret: boolean) =>
    hasCaret ? getShadowWithCaret(position) : getShadowWithoutCaret(position);

type PlacementMap = {
    [key in PopupPlacement]: PseudoPlacement;
};
const pseudoPlacementMapping: PlacementMap = {
    right: 'right',
    'right-start': 'right',
    'right-end': 'right',
    left: 'left',
    'left-start': 'left',
    'left-end': 'left',
    top: 'top',
    'top-start': 'top',
    'top-end': 'top',
    bottom: 'bottom',
    'bottom-start': 'bottom',
    'bottom-end': 'bottom',
};
const getPseudoPlacement = (position: PopupPlacement) => pseudoPlacementMapping[position];

const Container = styled.div<ContainerProps>`
    min-width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
    background-color: ${({ theme }) => theme.bento.colors.background.main};
    border-radius: 4px;
    ${({ placement, hasShadow, hasCaret }) => (hasShadow ? `box-shadow: ${getShadow(placement, hasCaret)};` : '')}
    transition: opacity 0.3s;
    z-index: 4;
    opacity: ${({ state }) => (isExitState(state) ? '0' : '1')};
    ${ModalWrapper} & {
        z-index: 1000;
    }
`;

const PortalWrapper: React.FC<{ portalRoot?: string }> = ({ children, portalRoot }) => {
    if (portalRoot) {
        const element = document.getElementById(portalRoot);
        return createPortal(children, element || document.body);
    }

    return <>{children}</>;
};

export const Popup: React.FC<PopupProps> = ({
    isOpen,
    placement = 'top',
    onClose,
    arrowSize = 8,
    portalRoot,
    parentRef,
    hasCaret = true,
    hasShadow = true,
    fullWidth = false,
    children,
}) => {
    const { bento } = React.useContext(ThemeContext);
    const ARROW_SIZE = hasCaret ? arrowSize : 0;
    const [width, setWidth] = React.useState('0');
    const [popperRef, setPopperRef] = React.useState<HTMLDivElement | null>(null);
    const [arrowRef, setArrowRef] = React.useState<HTMLDivElement | null>(null);

    const [measureHelperWidth, setMeasureHelperWidth] = React.useState<number | undefined>();

    const { styles, attributes, update } = usePopper(parentRef.current, popperRef, {
        placement,
        modifiers: hasCaret
            ? [
                  { name: 'arrow', options: { element: arrowRef } },
                  { name: 'offset', options: { offset: [0, ARROW_SIZE] } },
              ]
            : undefined,
    });
    const computedPlacement = getPseudoPlacement(
        ((attributes.popper && attributes.popper['data-popper-placement']) || placement) as PopupPlacement,
    );

    React.useEffect(() => {
        if (update) {
            update();
        }
        const onAppClick = (event: MouseEvent) => {
            if (!onClose || parentRef.current?.contains(event.target as Node)) {
                return;
            }

            if (
                isOpen &&
                event.target &&
                !parentRef.current?.contains(event.target as Node) &&
                !popperRef?.contains(event.target as Node)
            ) {
                onClose();
            }
        };

        window.addEventListener('mousedown', onAppClick);
        return () => {
            window.removeEventListener('mousedown', onAppClick);
        };
    }, [isOpen, !!parentRef.current]);

    React.useLayoutEffect(() => {
        if (!fullWidth) {
            // Dropdown will scale to fit its content
            if (measureHelperWidth) {
                setWidth(`${measureHelperWidth}px`);
            }
        } else if (portalRoot && parentRef.current) {
            // Dropdown will match the width of its trigger
            setWidth(`${parentRef.current.offsetWidth}px`);
        } else {
            // Dropdown won't have a specific width override set
            setWidth('100%');
        }
    }, [measureHelperWidth]);

    return (
        <PortalWrapper portalRoot={portalRoot}>
            <>
                <Transition in={isOpen} timeout={bento.transition}>
                    {(state: TransitionState) => (
                        <Container
                            ref={setPopperRef}
                            state={state}
                            hasShadow={hasShadow}
                            hasCaret={hasCaret}
                            placement={computedPlacement}
                            style={styles.popper}
                            fullWidth={fullWidth && !portalRoot}
                            {...attributes.popper}
                        >
                            {state !== 'exited' && hasCaret && (
                                <Caret
                                    ref={setArrowRef}
                                    placement={computedPlacement}
                                    size={ARROW_SIZE}
                                    style={styles.arrow}
                                />
                            )}
                            {state !== 'exited' ? <div style={{ width }}>{children}</div> : null}
                        </Container>
                    )}
                </Transition>
                <MeasureHelper onMeasure={m => setMeasureHelperWidth(m.width)}>{children}</MeasureHelper>
            </>
        </PortalWrapper>
    );
};
